import React, { useCallback, useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';

Chart.register(...registerables);

interface JobWagesBySectorProps {
  location: string;
}

// Main sectors from Census Bureau
const MAIN_SECTORS = [
  'Management, business, science, and arts occupations',
  'Service occupations',
  'Sales and office occupations',
  'Natural resources, construction, and maintenance occupations',
  'Production, transportation, and material moving occupations'
];

const SECTOR_SHORT_NAMES: Record<string, string> = {
  'Management, business, science, and arts occupations': 'Professional & Technical',
  'Service occupations': 'Service',
  'Sales and office occupations': 'Sales & Office',
  'Natural resources, construction, and maintenance occupations': 'Construction & Maintenance',
  'Production, transportation, and material moving occupations': 'Production & Transportation'
};

// Bar chart: median income by job sector for latest year
const JobWagesBySector: React.FC<JobWagesBySectorProps> = ({ location }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const creatingRef = useRef<boolean>(false);
  const [hasData, setHasData] = React.useState<boolean | null>(null);

  const buildChart = useCallback(async () => {
    if (!canvasRef.current) return;
    if (creatingRef.current) return;
    creatingRef.current = true;

    try {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      const existing = Chart.getChart(canvasRef.current as unknown as HTMLCanvasElement);
      if (existing) existing.destroy();
    } catch {}

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    await supabaseLocationService.initialize();
    const geofips = await supabaseLocationService.getGeofips(location);
    if (!geofips) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    const geoid = String(geofips);

    // Try querying by geofips (numeric) FIRST - this is the primary column
    let { data: years, error: yearsError } = await supabaseClient
      .from('job_by_wage_level')
      .select('year, geofips, location_name')
      .eq('geofips', geofips)
      .order('year', { ascending: false })
      .limit(1);

    // If no data by geofips, try geoid (text) as fallback
    if ((!years || years.length === 0) && !yearsError) {
      const geoidResult = await supabaseClient
        .from('job_by_wage_level')
        .select('year, geofips, location_name')
        .eq('geoid', geoid)
        .order('year', { ascending: false })
        .limit(1);
      
      if (geoidResult.data && geoidResult.data.length > 0) {
        years = geoidResult.data;
        yearsError = geoidResult.error;
      }
    }

    // If still no data, try location_name as fallback
    if ((!years || years.length === 0) && !yearsError) {
      const nameResult = await supabaseClient
        .from('job_by_wage_level')
        .select('year, geofips, location_name')
        .ilike('location_name', `%${location}%`)
        .order('year', { ascending: false })
        .limit(1);
      
      if (nameResult.data && nameResult.data.length > 0) {
        years = nameResult.data;
        yearsError = nameResult.error;
      }
    }

    if (yearsError) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    const year = years && years.length > 0 ? Number(years[0].year) : null;

    if (!year) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    // Use the geofips we found (might be from location_name lookup)
    const actualGeofips = years && years[0].geofips ? Number(years[0].geofips) : geofips;

    // Fetch all data for the year - use geofips (numeric) FIRST
    let { data: allWages, error: wagesError } = await supabaseClient
      .from('job_by_wage_level')
      .select('job_type, median_income, gender')
      .eq('geofips', actualGeofips)
      .eq('year', year)
      .order('median_income', { ascending: false });

    // If no data by geofips, try geoid (text) as fallback
    if ((!allWages || allWages.length === 0) && !wagesError) {
      const geoid = String(actualGeofips);
      const geoidResult = await supabaseClient
        .from('job_by_wage_level')
        .select('job_type, median_income, gender')
        .eq('geoid', geoid)
        .eq('year', year)
        .order('median_income', { ascending: false });
      
      if (geoidResult.data && geoidResult.data.length > 0) {
        allWages = geoidResult.data;
        wagesError = geoidResult.error;
      }
    }

    if (wagesError) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    if (!allWages || allWages.length === 0) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    // Filter to main sectors and gender='both' (or any if 'both' doesn't exist)
    const hasBothGender = allWages.some((w: any) => (w.gender || '').toLowerCase() === 'both');
    const wages = allWages.filter((r: any) => {
      const jobType = (r.job_type || '').trim();
      const gender = (r.gender || '').trim();
      // Use some() to handle potential whitespace differences
      const isMainSector = MAIN_SECTORS.some(sector => sector.trim() === jobType);
      // If 'both' exists in data, only use 'both'. Otherwise, use any gender.
      const genderMatch = hasBothGender ? gender.toLowerCase() === 'both' : true;
      return isMainSector && genderMatch;
    });

    if (!wages || wages.length === 0) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    setHasData(true);

    const sectors = wages.map((r: any) => SECTOR_SHORT_NAMES[r.job_type] || r.job_type);
    const incomes = wages.map((r: any) => Number(r.median_income || 0));

    // Color coding: green (high), blue (medium), orange (low)
    const colors = incomes.map((income, idx) => {
      const sorted = [...incomes].sort((a, b) => b - a);
      const rank = sorted.indexOf(income);
      if (rank < Math.ceil(sorted.length / 3)) return '#10b981'; // Green - top third
      if (rank < Math.ceil(sorted.length * 2 / 3)) return '#3b82f6'; // Blue - middle third
      return '#f59e0b'; // Orange - bottom third
    });

    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: sectors,
        datasets: [
          {
            label: 'Median Annual Income',
            data: incomes,
            backgroundColor: colors,
            borderColor: colors.map(c => c),
            borderWidth: 1,
          },
        ],
      },
      options: {
        indexAxis: 'y', // Horizontal bars
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: standardLegend,
          title: standardTitle(`Median Income by Sector - ${location}`),
          tooltip: {
            callbacks: {
              label: (ctx: any) => `$${(ctx.parsed.x || 0).toLocaleString()}`,
            }
          }
        },
        scales: {
          x: {
            title: { display: true, text: 'Median Annual Income (USD)' },
            beginAtZero: true,
          },
          y: {
            title: { display: true, text: 'Economic Sector' },
            ticks: { autoSkip: false },
          },
        },
        interaction: { mode: 'index', intersect: false },
      },
    });

    creatingRef.current = false;
  }, [location]);

  useEffect(() => {
    buildChart();
    const canvasNode = canvasRef.current;
    return () => {
      try {
        if (chartRef.current) chartRef.current.destroy();
        if (canvasNode) {
          const existing = Chart.getChart(canvasNode as unknown as HTMLCanvasElement);
          if (existing) existing.destroy();
        }
      } catch {}
    };
  }, [buildChart]);

  if (hasData === false) {
    return (
      <div className="chart-container" style={{ padding: '40px', textAlign: 'center' }}>
        <p>No job wages data available for {location}.</p>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <canvas ref={canvasRef} style={{ height: '400px' }} />
    </div>
  );
};

export default JobWagesBySector;

